import React from "react";
import type { BuilderItem } from "../../types/gangsheet";
import { getDpi, getDpiTone, normalizeAngle } from "../../utils/gangsheetMath";
import { Badge, panelStyle, SectionTitle, subtlePanelStyle } from "./GangsheetUI";

type Props = {
  items: BuilderItem[];
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  overlapMap: Set<string>;
  outOfBoundsMap: Set<string>;
  isMobile: boolean;
};

export default function GangsheetArtworkList({
  items,
  selectedId,
  setSelectedId,
  overlapMap,
  outOfBoundsMap,
  isMobile,
}: Props) {
  return (
    <div style={{ ...panelStyle, padding: 14, display: "grid", gap: 14 }}>
      <SectionTitle
        title="Loaded Artwork"
        subtitle="Tap a piece to select it."
      />

      <div
        style={{
          display: "grid",
          gap: 10,
          maxHeight: isMobile ? "none" : 560,
          overflowY: "auto",
        }}
      >
        {items.length === 0 ? (
          <div
            style={{
              ...subtlePanelStyle,
              padding: 16,
              color: "#93c5fd",
              lineHeight: 1.5,
            }}
          >
            No artwork loaded yet.
          </div>
        ) : (
          items.map((item) => {
            const dpi = getDpi(item);
            const tone = getDpiTone(dpi);
            const overlapping = overlapMap.has(item.id);
            const outOfBounds = outOfBoundsMap.has(item.id);
            const isSelectedItem = selectedId === item.id;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedId(item.id)}
                style={{
                  ...subtlePanelStyle,
                  padding: 12,
                  textAlign: "left",
                  cursor: "pointer",
                  border: isSelectedItem
                    ? "1px solid rgba(59,130,246,0.5)"
                    : overlapping || outOfBounds
                    ? "1px solid rgba(239,68,68,0.35)"
                    : "1px solid rgba(96,165,250,0.14)",
                  boxShadow: isSelectedItem
                    ? "0 0 0 1px rgba(59,130,246,0.2)"
                    : "none",
                }}
              >
                <div
                  style={{
                    fontWeight: 900,
                    color: "#eff6ff",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                  }}
                >
                  {item.name}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 10,
                  }}
                >
                  <Badge
                    text={`${dpi} DPI`}
                    color={tone.color}
                    bg={tone.bg}
                    border={tone.border}
                  />
                  <Badge
                    text={`${item.widthIn.toFixed(2)}" × ${item.heightIn.toFixed(2)}"`}
                    color="#bfdbfe"
                    bg="rgba(37,99,235,0.12)"
                    border="rgba(96,165,250,0.24)"
                  />
                  <Badge
                    text={`${Math.round(normalizeAngle(item.rotation))}°`}
                    color="#c4b5fd"
                    bg="rgba(139,92,246,0.12)"
                    border="rgba(139,92,246,0.24)"
                  />
                  {overlapping && (
                    <Badge
                      text="Overlap"
                      color="#fca5a5"
                      bg="rgba(239,68,68,0.14)"
                      border="rgba(239,68,68,0.3)"
                    />
                  )}
                  {outOfBounds && (
                    <Badge
                      text="Out of bounds"
                      color="#fca5a5"
                      bg="rgba(239,68,68,0.14)"
                      border="rgba(239,68,68,0.3)"
                    />
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}